"use client"

import * as React from "react"
import * as ProgressPrimitive from "@radix-ui/react-progress"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@workspace/ui/lib/utils"

const progressVariants = cva(
  "relative w-full overflow-hidden rounded-full border border-gray-200 dark:border-gray-700 ring-1 ring-white/10 dark:ring-white/5 shadow-xs",
  {
    variants: {
      variant: {
        default: "bg-primary/20 dark:bg-primary/15",
        secondary: "bg-secondary/30 dark:bg-secondary/25",
        accent: "bg-accent/20 dark:bg-accent/15",
        muted: "bg-muted/50 dark:bg-muted/30",
      },
      size: {
        default: "h-2",
        sm: "h-1.5",
        lg: "h-3",
        xl: "h-4",
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const progressIndicatorVariants = cva(
  "h-full w-full flex-1 transition-transform duration-500 rounded-full",
  {
    variants: {
      variant: {
        default: "bg-primary",
        secondary: "bg-secondary",
        accent: "bg-accent",
        muted: "bg-muted-foreground",
      }
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

interface ProgressProps extends 
  React.ComponentProps<typeof ProgressPrimitive.Root>,
  VariantProps<typeof progressVariants> {}

function Progress({
  className,
  variant,
  size,
  value,
  ...props
}: ProgressProps) {
  return (
    <ProgressPrimitive.Root
      data-slot="progress"
      className={cn(progressVariants({ variant, size }), className)}
      {...props}
    >
      <ProgressPrimitive.Indicator
        data-slot="progress-indicator"
        className={cn(progressIndicatorVariants({ variant }))}
        style={{ transform: `translateX(-${100 - (value || 0)}%)` }}
      />
    </ProgressPrimitive.Root>
  )
}

export { Progress }
